import { useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { markSignedOut, signedInAccount } from '../auth/ssoMemory';
import { initials } from '../lib/display';
import { usePortfolio } from '../lib/portfolio-context';

/** The WISE Launchpad, the platform's PROD home (FacilityWISE is prod-only); the host here before was never provisioned, so the link was dead. */
const WISE_HOME = 'https://wise.westportproperties.net';

/**
 * The avatar in the chrome, made to mean something.
 *
 * It used to be a coloured circle with initials in it and no behaviour at all —
 * it looked like every other account control on the platform and did nothing
 * when clicked, which is worse than not being there. It now answers the four
 * questions someone actually opens an avatar for: who am I signed in as, what
 * am I allowed to see, how old is this data, and how do I leave.
 *
 * Freshness belongs here rather than only in the page footer because this data
 * is a 15-minute-old projection of a nightly replica. Someone comparing this
 * screen against Yardi needs to know how old it is without hunting for it.
 */
export function AccountMenu() {
  const { instance, accounts } = useMsal();
  const { data, error } = usePortfolio();
  const builtAt = data?.builtAt ?? null;
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // The ACTIVE account — the link's person, or whoever just signed in — never
  // simply the first cached account (auth/ssoMemory.ts).
  const account = signedInAccount(instance, accounts);
  const name = account?.name || '';
  const upn = account?.username || '';

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Stated as an absolute local time, not "2 hours ago". A relative age is
  // read as freshness; an absolute one is comparable against the Yardi screen
  // sitting next to it, which is what people are actually doing here.
  const asOf = builtAt
    ? new Date(builtAt).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : null;

  return (
    <div className="acct" ref={wrapRef}>
      <button
        className="cav"
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={name ? `Account — ${name}` : 'Account'}
        title={name || upn}
      >
        {name || upn ? initials(name || upn) : '—'}
      </button>

      {open && (
        <div className="acct-pop" role="menu">
          <div className="acct-id">
            <div className="acct-n">{name || 'Signed in'}</div>
            {upn && <div className="acct-u">{upn}</div>}
          </div>

          <div className="acct-sec">
            <div className="acct-row">
              <span>Access</span>
              <b>FacilityWISE viewer</b>
            </div>
            <div className="acct-row">
              <span>Data as of</span>
              <b>{asOf ?? (error ? 'unavailable' : 'loading')}</b>
            </div>
          </div>

          <div className="acct-act">
            <a className="acct-b" href={WISE_HOME} target="_blank" rel="noreferrer">
              WISE Home <span aria-hidden="true">↗</span>
            </a>
            <button
              className="acct-b"
              type="button"
              // A CHOICE: remembered, so no link signs them straight back in
              // (auth/ssoMemory.ts) — until they sign in again.
              onClick={() => {
                markSignedOut();
                void instance.logoutRedirect({ account });
              }}
            >
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
